/**
 * CONSAI — Premium Trial Nudge Popup (dark glow)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { GlowCard } from './ui/glow-card';
import { X, Crown, Sparkles, Check } from 'lucide-react';
import { getFeatureColor } from '../lib/feature-colors';

const PremiumTrialNudgePopup = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [processing, setProcessing] = useState(false);

  const STORAGE_KEY = 'consai_nudge_data';
  const MAX_DAILY_SHOWS = 2;

  const EXCLUDED_PATHS = ['/login', '/signup', '/forgot-password', '/auth/callback', '/admin'];
  const isOnExcludedPath = EXCLUDED_PATHS.some(p => location.pathname.startsWith(p));

  const PAID_PLANS = ['starter', 'premium'];
  const shouldNudge = user && !isOnExcludedPath && !PAID_PLANS.includes(user.current_plan);

  const getTodayStr = () => new Date().toISOString().split('T')[0];

  const getNudgeData = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.date !== getTodayStr()) return { date: getTodayStr(), count: 0, lastShown: 0 };
        return data;
      }
    } catch (e) { console.error('Error reading nudge data:', e); }
    return { date: getTodayStr(), count: 0, lastShown: 0 };
  }, []);

  const saveNudgeData = useCallback((data) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    if (!shouldNudge) return;
    const nudgeData = getNudgeData();
    if (nudgeData.count >= MAX_DAILY_SHOWS) return;
    const isNewSignup = sessionStorage.getItem('signup_session') === 'true';
    const minDelay = nudgeData.count === 0 ? (isNewSignup ? 15000 : 1500) : 120000;
    const maxDelay = nudgeData.count === 0 ? (isNewSignup ? 15000 : 3000) : 300000;
    const randomDelay = Math.floor(Math.random() * (maxDelay - minDelay)) + minDelay;
    const timer = setTimeout(() => {
      const currentData = getNudgeData();
      if (currentData.count < MAX_DAILY_SHOWS) {
        setIsVisible(true);
        if (isNewSignup) sessionStorage.removeItem('signup_session');
        saveNudgeData({ ...currentData, count: currentData.count + 1, lastShown: Date.now() });
      }
    }, randomDelay);
    return () => clearTimeout(timer);
  }, [shouldNudge, getNudgeData, saveNudgeData]);

  const handleSelectPlan = (planType) => {
    setProcessing(true); setIsVisible(false);
    navigate(`/payment?type=${planType}`);
  };

  if (!isVisible || !shouldNudge) return null;

  const starterFeatures = [
    language === 'hi' ? '50 ग्राहकों तक' : 'Up to 50 customers',
    language === 'hi' ? 'बेसिक स्टॉक ट्रैकिंग' : 'Basic stock tracking',
    language === 'hi' ? '10 AI प्रश्न/सप्ताह' : '10 AI questions/week',
  ];
  const premiumFeatures = [
    language === 'hi' ? 'असीमित ग्राहक' : 'Unlimited customers',
    language === 'hi' ? 'AI असिस्टेंट & एनालिटिक्स' : 'AI Assistant & Analytics',
    language === 'hi' ? '50 AI प्रश्न/दिन' : '50 AI questions/day',
    language === 'hi' ? 'इनवॉइस जनरेशन' : 'Invoice generation',
  ];

  const blue = getFeatureColor('analytics');
  const ai = getFeatureColor('ai');

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in" data-testid="premium-nudge-popup">
      <div className="relative w-full max-w-lg animate-in zoom-in-95 duration-300">
        <GlowCard feature="ai" size="auto" innerClassName="p-0 overflow-hidden">
          <button onClick={() => setIsVisible(false)}
            className="absolute top-3 right-3 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors z-10"
            data-testid="nudge-skip-btn">
            <X className="h-4 w-4 text-white/70" />
          </button>

          <div className="p-6 text-center"
            style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.18) 0%, rgba(67,97,238,0.10) 100%)' }}>
            <Sparkles className="h-8 w-8 mx-auto mb-2 text-yellow-300" />
            <h2 className="text-xl font-semibold text-white tracking-tight">
              {language === 'hi' ? 'ट्रायल समाप्त होने से पहले प्लान चुनें!' : 'Choose a Plan Before Your Trial Ends!'}
            </h2>
            <p className="text-white/65 text-sm mt-1">
              {language === 'hi' ? 'अपने व्यवसाय को बढ़ाते रहें' : 'Keep growing your business'}
            </p>
          </div>

          <div className="p-4 grid grid-cols-2 gap-3">
            {/* Starter */}
            <button onClick={() => handleSelectPlan('starter')}
              className="relative rounded-2xl p-4 text-left transition-all hover:-translate-y-0.5"
              style={{ background: blue.tint, border: `1.5px solid ${blue.border}` }}
              data-testid="nudge-starter-plan">
              <p className="text-base font-semibold" style={{ color: blue.text }}>
                {language === 'hi' ? 'स्टार्टर' : 'Starter'}
              </p>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-2xl font-bold text-white">₹19</span>
                <span className="text-white/50 text-xs">/{language === 'hi' ? 'माह' : 'mo'}</span>
              </div>
              <ul className="mt-3 space-y-1.5">
                {starterFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-white/70">
                    <Check className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: blue.text }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 h-9 rounded-lg flex items-center justify-center text-sm font-medium"
                style={{ background: blue.hex, color: '#FFFFFF' }}>
                {language === 'hi' ? 'चुनें' : 'Select'}
              </div>
            </button>

            {/* Premium */}
            <button onClick={() => handleSelectPlan('premium')}
              className="relative rounded-2xl p-4 text-left transition-all hover:-translate-y-0.5"
              style={{ background: ai.tint, border: `1.5px solid ${ai.border}` }}
              data-testid="nudge-premium-plan">
              <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                <Badge className="bg-amber-500 text-white text-[10px] px-2 py-0.5 border-0">
                  {language === 'hi' ? 'लोकप्रिय' : 'Popular'}
                </Badge>
              </div>
              <div className="flex items-center gap-1 pt-2">
                <Crown className="h-4 w-4" style={{ color: ai.text }} />
                <p className="text-base font-semibold" style={{ color: ai.text }}>
                  {language === 'hi' ? 'प्रीमियम' : 'Premium'}
                </p>
              </div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-2xl font-bold text-white">₹99</span>
                <span className="text-white/50 text-xs">/{language === 'hi' ? 'माह' : 'mo'}</span>
              </div>
              <ul className="mt-3 space-y-1.5">
                {premiumFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-white/70">
                    <Check className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: ai.text }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 h-9 rounded-lg flex items-center justify-center text-sm font-medium"
                style={{ background: ai.hex, color: '#FFFFFF' }}>
                {language === 'hi' ? 'चुनें' : 'Select'}
              </div>
            </button>
          </div>

          <div className="px-4 pb-4 text-center">
            <p className="text-xs text-white/40">
              {language === 'hi' ? 'सुरक्षित भुगतान। कभी भी रद्द करें।' : 'Secure payment. Cancel anytime.'}
            </p>
          </div>
        </GlowCard>
      </div>
    </div>
  );
};

export default PremiumTrialNudgePopup;
